import React from "react";
import { Info } from "lucide-react";
import { ResponsiveContainer } from "recharts";

/** KPI card with optional tooltip */
export function KpiCard({ title, value, subtitle, tip, accent = "indigo" }) {
  const accents = {
    indigo: "from-indigo-500 to-indigo-700",
    emerald: "from-emerald-500 to-teal-600",
    amber: "from-amber-500 to-orange-600",
    rose: "from-rose-500 to-pink-600",
    sky: "from-sky-500 to-blue-600",
    cyan: "from-cyan-500 to-teal-600",
    violet: "from-violet-500 to-purple-600",
  };

  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm pr-3 p-2 md:p-3 overflow-hidden min-w-0">
      <div className={`absolute inset-y-0 right-0 w-1.5 bg-gradient-to-b ${accents[accent] || accents.indigo}`} />
      <div className="flex items-start justify-between gap-1.5">
        <p className="text-[16px] leading-snug md:text-sm text-slate-500 font-medium line-clamp-2">{title}</p>
        {tip && (
          <span className="group relative shrink-0 text-slate-400 hover:text-slate-600 cursor-help hidden md:inline-flex" title={tip}>
            <Info size={16} />
            <span className="pointer-events-none absolute left-0 top-6 z-20 hidden group-hover:block w-56 rounded-lg bg-slate-800 text-white text-xs p-2 shadow-lg leading-relaxed">
              {tip}
            </span>
          </span>
        )}
      </div>
      <p className="mt-2 text-md leading-tight md:text-md font-bold text-slate-900 tracking-tight break-words">{value}</p>
      <p className="text-[12px] md:text-lg text-slate-500 leading-snug line-clamp-3">----</p>

      {subtitle && <p className="mt-2 text-md leading-tight md:text-md font-bold text-slate-900 tracking-tight break-words">{subtitle}</p>}
    </div>
  );
}

export function SectionCard({ title, subtitle, action, children, className = "" }) {
  return (
    <section className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6 min-w-0 overflow-hidden ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 md:gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-base md:text-xl font-bold text-slate-800">{title}</h2>
          {subtitle && <p className="mt-1 text-[11px] md:text-xs leading-5 text-slate-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ message = "لا توجد بيانات لهذه الفترة" }) {
  return (
    <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
      {message}
    </div>
  );
}

export function LoadingBlock() {
  return (
    <div className="animate-pulse space-y-3 py-6">
      <div className="h-4 bg-slate-100 rounded w-1/3" />
      <div className="h-32 bg-slate-100 rounded-xl" />
      <div className="h-4 bg-slate-100 rounded w-1/2" />
    </div>
  );
}

/** Stable chart host — avoids Recharts resize loops on mobile. */
export function ChartBox({ className = "h-64 md:h-80", children }) {
  return (
    <div className={`w-full min-w-0 overflow-hidden ${className}`} dir="ltr">
      <ResponsiveContainer width="100%" height="100%" debounce={300} minWidth={0}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}
